// src/components/servicing/sections/ServicesSection.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  WrenchScrewdriverIcon, 
  Cog6ToothIcon, 
  ShieldCheckIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  PaintBrushIcon,
  BoltIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

const ServicesSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const services = [
    { 
      id: 1, 
      name: 'Basic Service', 
      icon: WrenchScrewdriverIcon, 
      price: '₹499',
      description: 'Oil change, filter check'
    },
    { 
      id: 2, 
      name: 'Premium Service', 
      icon: Cog6ToothIcon, 
      price: '₹999',
      description: 'Complete tune-up'
    },
    { 
      id: 3, 
      name: 'Engine Repair', 
      icon: ShieldCheckIcon, 
      price: '₹1499',
      description: 'Fix engine issues'
    },
    { 
      id: 4, 
      name: 'Brake Service', 
      icon: BoltIcon, 
      price: '₹699',
      description: 'Brake pad replacement'
    },
    { 
      id: 5, 
      name: 'Bike Detailing', 
      icon: SparklesIcon, 
      price: '₹599',
      description: 'Deep cleaning & polish'
    },
    { 
      id: 6, 
      name: 'Bike Spa', 
      icon: PaintBrushIcon, 
      price: '₹799',
      description: 'Complete wash & wax'
    },
    { 
      id: 7, 
      name: 'Inspection', 
      icon: MagnifyingGlassIcon, 
      price: '₹299',
      description: 'Full safety check'
    },
    { 
      id: 8, 
      name: 'Pickup/Drop', 
      icon: TruckIcon, 
      price: 'Free',
      description: 'Doorstep service'
    },
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Rohan Kulkarni',
      location: 'Kothrud, Pune',
      rating: 5,
      image: 'https://ui-avatars.com/api/?name=Rohan+Kulkarni&background=3B82F6&color=fff',
      text: 'Exceptional service! My bike feels brand new after their premium package. The team was professional and transparent about every repair.'
    },
    {
      id: 2,
      name: 'Ananya Mehta',
      location: 'Baner, Pune',
      rating: 5,
      image: 'https://ui-avatars.com/api/?name=Ananya+Mehta&background=10B981&color=fff',
      text: 'Best bike service in Pune. Free pickup and delivery made it so convenient. Highly recommend their detailing service!'
    },
    {
      id: 3,
      name: 'Vikram Deshmukh',
      location: 'Viman Nagar, Pune',
      rating: 5,
      image: 'https://ui-avatars.com/api/?name=Vikram+Deshmukh&background=F59E0B&color=fff',
      text: 'Quick turnaround and fair pricing. The mechanics are knowledgeable and explain everything clearly. Will definitely use again.'
    },
    {
      id: 4,
      name: 'Neha Patwardhan',
      location: 'Koregaon Park, Pune',
      rating: 5,
      image: 'https://ui-avatars.com/api/?name=Neha+Patwardhan&background=EF4444&color=fff',
      text: 'Impressed with the quality of work and genuine parts. My daily commute is much smoother now. Great experience overall!'
    },
    {
      id: 5,
      name: 'Arjun Patel',
      location: 'Hinjewadi, Pune',
      rating: 5,
      image: 'https://ui-avatars.com/api/?name=Arjun+Patel&background=8B5CF6&color=fff',
      text: 'Professional team with attention to detail. They fixed issues I didn\'t even know existed. Worth every rupee!'
    }
  ];

  const stats = [
    { value: '5K+', label: 'Happy Customers', icon: '😊' },
    { value: '25+', label: 'Service Centers', icon: '🏢' },
    { value: '4.9★', label: 'Average Rating', icon: '⭐' },
    { value: '100%', label: 'Genuine Parts', icon: '✓' }
  ];

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <StarSolidIcon 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <>
      {/* Services Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          
          {/* Section Header */}
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Professional bike care services with transparent pricing and certified mechanics
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Link 
                  to="/" 
                  key={service.id} 
                  className="group bg-white border-2 border-gray-200 rounded-2xl p-5 sm:p-6 hover:border-blue-600 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-all">
                      <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1">
                      {service.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 mb-2">
                      {service.description}
                    </p>
                    <span className="text-blue-600 font-bold text-sm sm:text-base">
                      {service.price}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-6 text-center hover:border-blue-600 hover:shadow-lg transition-all">
                <div className="text-3xl mb-2">{stat.icon}</div>
                <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1">
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-16 bg-blue-600">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          
          {/* Section Header */}
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Customer Stories
            </h2>
            <p className="text-base sm:text-lg text-blue-100">
              Real reviews from real riders in Pune
            </p>
          </div>

          {/* Testimonial Card */}
          <div className="relative">
            <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10">
              
              {/* Profile Image */}
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src={testimonials[activeTestimonial].image}
                  alt={testimonials[activeTestimonial].name}
                  className="w-16 h-16 rounded-full border-4 border-blue-100"
                />
                <div className="flex-1">
                  <div className="font-bold text-gray-900 text-lg">
                    {testimonials[activeTestimonial].name}
                  </div>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <span>📍</span>
                    {testimonials[activeTestimonial].location}
                  </div>
                </div>
                <div className="flex gap-0.5">
                  {renderStars(testimonials[activeTestimonial].rating)}
                </div>
              </div>

              {/* Quote */}
              <div className="relative">
                <div className="absolute -top-4 -left-2 text-6xl text-blue-100 font-serif">"</div>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed pl-6">
                  {testimonials[activeTestimonial].text}
                </p>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-6 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-blue-50 transition-all shadow-lg text-blue-600"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-6 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-blue-50 transition-all shadow-lg text-blue-600"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`h-2 rounded-full transition-all ${
                  activeTestimonial === index 
                    ? 'w-8 bg-white' 
                    : 'w-2 bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>

        </div>
      </section>
    </>
  );
};

export default ServicesSection;
